'use client';

import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { DateFormats } from '@repo/constants';
import {
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
  Skeleton,
} from '@ui/components';
import { cn } from '@ui/lib/utils';
import dayjs from 'dayjs';
import customDateFormat from 'dayjs/plugin/customParseFormat';
import { CalendarIcon, XIcon } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo } from 'react';

dayjs.extend(customDateFormat);

interface DatePickerProps {
  id?: string;
  className?: string;
  label?: string;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  maxDateError?: string;
  value?: string;
  onChange: (date: string) => void;
  disabled?: boolean;
  weekendAllowed?: boolean;
  allowEmpty?: boolean;
  clearable?: boolean;
  isSkeleton?: boolean;
  popoverContentProps?: PopoverContentProps;
}

const convertDateToPresentation = (value: string) =>
  value
    ? dayjs(value, DateFormats.DEFAULT).format(DateFormats.DEFAULT_PRESENTATION)
    : '';

export const DatePicker: FC<DatePickerProps> = (props) => {
  const {
    className,
    label = '',
    placeholder = 'Select date',
    weekendAllowed,
    value,
    minDate = '1900-01-01',
    maxDate = '2099-01-01',
    disabled,
    isSkeleton,
    onChange,
    allowEmpty = false,
    clearable = false,
    popoverContentProps,
  } = props;

  const options = useMemo(
    () =>
      maskitoDateOptionsGenerator({
        mode: 'dd/mm/yyyy',
        separator: '/',
        min: dayjs(minDate).toDate(),
        max: dayjs(maxDate).toDate(),
      }),
    [maxDate, minDate],
  );

  const fromYear = useMemo(
    () => dayjs(minDate, DateFormats.DEFAULT).year(),
    [minDate],
  );
  const toYear = useMemo(
    () => dayjs(maxDate, DateFormats.DEFAULT).year(),
    [maxDate],
  );

  const selectedDate = useMemo(() => {
    if (!value) {
      return undefined;
    }
    const parsed = dayjs(value, DateFormats.DEFAULT);
    return parsed.isValid() ? parsed.toDate() : undefined;
  }, [value]);

  const [isOpen, setIsOpen] = React.useState(false);

  const inputRef = useMaskito({ options });

  const [inputValue, setInputValue] = React.useState('');

  const handleOpen = useCallback(
    (isOpen: boolean) => {
      if (disabled) return;
      setIsOpen(isOpen);
    },
    [disabled],
  );

  const handleSelect = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) {
        if (allowEmpty) {
          onChange('');
          setInputValue('');
        }
        return;
      }

      onChange(dayjs(selectedDate).format(DateFormats.DEFAULT));
      setIsOpen(false);
    },
    [allowEmpty, onChange],
  );

  const handleClear = useCallback(() => {
    onChange('');
    setInputValue('');
    setIsOpen(false);
  }, [onChange]);

  const handleChangeByInput = () => {
    if (!inputValue.trim()) {
      if (allowEmpty) {
        onChange('');
        setInputValue('');
      } else if (dayjs(value, DateFormats.DEFAULT).isValid()) {
        setInputValue(convertDateToPresentation(value ?? ''));
      } else {
        setInputValue('');
      }
      return;
    }

    const _date = dayjs(inputValue, DateFormats.DEFAULT_PRESENTATION);
    const dayOfWeek = _date.day();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (_date.isValid()) {
      if (!weekendAllowed && isWeekend) {
        const adjustedDate =
          dayOfWeek === 0 ? _date.subtract(2, 'day') : _date.subtract(1, 'day');
        const adjustedValue = adjustedDate.format(DateFormats.DEFAULT);
        onChange(adjustedValue);
        setInputValue(convertDateToPresentation(adjustedValue));
        return;
      }

      onChange(_date.format(DateFormats.DEFAULT));
    } else if (dayjs(value, DateFormats.DEFAULT).isValid()) {
      setInputValue(convertDateToPresentation(value ?? ''));
    }
  };

  useEffect(() => {
    setInputValue(convertDateToPresentation(value ?? ''));
  }, [value]);

  const canClear = clearable && allowEmpty && Boolean(value) && !disabled;

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpen}
    >
      {isSkeleton ? (
        <Skeleton
          className={cn('w-[200px] h-10 text-left rounded-md', className)}
        />
      ) : (
        <div
          className={cn(
            'flex flex-col gap-2',
            className,
            disabled && 'cursor-not-allowed',
          )}
        >
          {!!label && (
            <Label
              className="text-xs font-semibold"
              htmlFor="calendar"
            >
              {label}
            </Label>
          )}
          <div
            className={cn(
              'flex h-10 w-[200px] flex-row items-center justify-between overflow-hidden rounded-md border border-input bg-background pr-1 text-left font-normal hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
              className,
            )}
          >
            <input
              ref={inputRef}
              className={cn(
                'flex ml-2 pl-2 flex-1 bg-transparent outline-0 w-full',
                disabled && 'cursor-not-allowed disabled:opacity-disabled',
              )}
              disabled={disabled}
              id="calendar-input"
              placeholder={placeholder}
              value={inputValue}
              onBlur={handleChangeByInput}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInputValue(e.target?.value)
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleChangeByInput();
                }
              }}
            />
            {canClear && (
              <button
                aria-label={`Clear ${label || 'date'}`}
                className="cursor-pointer p-2 opacity-70 hover:opacity-100"
                type="button"
                onClick={handleClear}
                onMouseDown={(event) => event.preventDefault()}
              >
                <XIcon className="size-4 min-w-4" />
              </button>
            )}
            <PopoverTrigger
              disabled={disabled}
              asChild
            >
              <div
                className={cn(
                  'cursor-pointer p-2',
                  disabled && 'cursor-not-allowed',
                )}
              >
                <CalendarIcon
                  className={cn(
                    'size-4 min-w-4',
                    disabled && 'opacity-disabled',
                  )}
                />
              </div>
            </PopoverTrigger>
          </div>
        </div>
      )}
      <PopoverContent
        {...popoverContentProps}
        className="mx-4 w-auto p-0"
      >
        <Calendar
          captionLayout="dropdown"
          defaultMonth={selectedDate}
          fromYear={fromYear}
          id={props?.id ?? 'calendar'}
          maxDate={maxDate}
          minDate={minDate}
          mode="single"
          selected={selectedDate}
          toYear={toYear}
          weekendAllowed={weekendAllowed}
          initialFocus
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
};
